import { FileModel, SelectAllDto } from '@osos/shared/api';
import { ActionInfoModel } from '../action-info.model';
import {
  EntryWorkflowCurrentStep,
  EntryWorkflowHistory,
} from '../entry-workflow-steps.models';

import { DataEntryStateEnum } from '../enums/data-entry-state.enum';
import { ShowEmailConfigTypeEnum } from '../enums/show-email-config-type-enum';
import { RefProperty } from '../ref-property.model';
import { FieldPresenterModel } from './field-presenter.model';
import { WorkflowAdhocStepPresenterModel } from './workflow-presenter-models/workflow-step-presenter.model';

export class DataEntryPresenterModel {
  id?: string;
  entityVersion?: number;
  specificationVersion?: number;
  dataEntity: string;
  value: {
    [key: string]: RefProperty | any;
    _metaWorkflowHistory?: EntryWorkflowHistory[];
    _metaCurrentSteps?: EntryWorkflowCurrentStep[];
    _metaAdhocSteps?: WorkflowAdhocStepPresenterModel[];
  };
  index?: number;
  state: DataEntryStateEnum;
  creationInfo?: {
    doneAt: string;
    doneBy: RefProperty;
  };
  updateInfo?: {
    doneAt: string;
    doneBy: RefProperty;
  };
  history?: Array<EntryHistoryPresenterModel>;
  isEntryObsolete?: boolean;
  isEntryInvalid?: boolean;
  isImplicit?: boolean;
  _obsoleteValue?: {
    [key: string]: RefProperty | any;
    _metaWorkflowHistory?: EntryWorkflowHistory[];
    _metaCurrentSteps?: EntryWorkflowCurrentStep[];
    _metaAdhocSteps?: WorkflowAdhocStepPresenterModel[];
  };
}

export class EntryHistoryPresenterModel {
  actionInfo: ActionInfoModel;
  additionalData?: any;
  historyType: 0 | 1 | 2 | 3 | 4 | 5;
  invalidReason?: 0 | 1 | null;
  [key: string]: any;
}
export class DataEntryUpdatePositionPresenterModel {
  id?: string;
  categoryField: string;
  changePriority?: boolean;
  column: string;
  direction?: 1 | -1;
  nextId?: string;
  previousId?: string;
  isKanban?: boolean;
}

export interface EntryAttachments {
  fieldFullNormalizedName: string;
  files?: FileModel[];
}

export interface DataEntryNavigationInfoModel {
  /**
   * The index of the entry in the list of entries
   */
  index: number;
  /**
   * The id of the previous entry
   */
  previousId: string;
  /**
   * The id of the next entry
   */
  nextId: string;

  /**
   * The total number of navigation entries
   */
  totalCount: number;
}
export interface ReferenceValueUpdateModel {
  fieldNormalizedName: string;
  /**
   * The data entry id to update
   */
  dataEntryId: string;
  /**
   * The patch operations to apply on the referenced entry
   */
  jsonPatch: {
    op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
    path: string;
    value: any;
  }[];
}
export interface MappingFieldSourcesModel {
  mappedFieldId: string;
  mappedFieldValue: any;
}
export interface KanbanEntriesPresenterModel {
  [key: string]: {
    items: DataEntryPresenterModel[];
    totalCount: number;
  };
}
export class EntryEmailsPresenterModel {
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  generatedAt: string;
  attachments: FileModel[];
  generatedBy: RefProperty;
  relatedTriggerExecutionId: string;
  source: ShowEmailConfigTypeEnum;
}

export class TransformationsWithVersionPresenterModel {
  sourceVersion: number;
  transformations: Array<TransformationPresenterModel>;
}

export class BulkUpgradeEntriesPresenterModel {
  /**
   * @deprecated Use requiredTransformations and additionalTransformations instead for v2 endpoint
   */
  entryVersion?: number;

  /**
   * If this flag is false and the entity has workflow enabled, an error will be returned.
   */
  resetWorkflow: boolean;
  dataEntriesIds?: string[];
  selectAllModel?: SelectAllDto;

  /**
   * @deprecated Use requiredTransformations and additionalTransformations instead for v2 endpoint
   */
  transformationModel?: Array<TransformationPresenterModel>;

  /**
   * Required transformations grouped by source version (for v2 endpoint)
   */
  requiredTransformations?: Array<TransformationsWithVersionPresenterModel>;

  /**
   * Additional transformations grouped by source version (for v2 endpoint)
   */
  additionalTransformations?: Array<TransformationsWithVersionPresenterModel>;
}

export class BulkUpdateEntriesPresenterModel {
  dataEntriesIds?: string[];

  /**
   * If this flag is false, invalid entries will not be saved.
   */
  saveInvalidEntries: boolean;
  selectAllModel?: SelectAllDto;
  updateDefinitions: Array<BulkUpdateDefinition>;
}
export class TransformationPresenterModel {
  /**
   * The field full normalized.
   */
  field: string;
  type: TransformationTypeEnum;

  /**
   * The value associated with the transformation.
   */
  value?: any;
}
export interface TransformationWithVersion
  extends TransformationPresenterModel {
  sourceVersion?: number;
}
export class BulkUpdateDefinition {
  /**
   * The field full normalized.
   */
  field: string;
  type: BulkUpdateFieldValueSourceEnum;

  /**
   * The value associated with the transformation.
   */
  value?: any;
}
export class DataEntityVersionChangesPresenterModel {
  changeType:
    | 'Field type changed'
    | 'Field restored from deleted but it breaks some rules'
    | 'Added required field'
    | 'Change field to required'
    | 'Added unique field'
    | 'Change field to unique'
    | 'Change field to repeated'
    | 'Change field to mulitvalue'
    | 'Entity workflow changed'
    | 'Enable deny circular relation on field'
    | 'Reference source changed';
  field?: FieldPresenterModel;
  isBreaking: boolean;
}

export enum TransformationTypeEnum {
  Fixed = 0,
  Dynamic = 1,
  Expression = 2,
  SystemBehavior = 3,
  Default = 4,
  NotSet = 5,
}

export enum BulkUpdateFieldValueSourceEnum {
  Fixed = 0,
  Expression = 1,
  Default = 2,
  Null = 3,
}

export class BulkUpgradeResponsePresenterModel {
  failures: Array<BulkUpgradeFailurePresenterModel>;
  successfulUpgrades: number;
}

export class BulkUpgradeFailurePresenterModel {
  entryId: string;
  errors: string[];
}

export class BulkUpdateResponsePresenterModel {
  failures: Array<BulkUpdateFailurePresenterModel>;
  savedAsInvalid: number;
  successfulUpdates: number;
}

export class BulkUpdateFailurePresenterModel {
  entryId: string;
  errors: string[];
}
export class BulkEntryErrors {
  [key: string]: {
    success: boolean;
    errorCode?: string;
  };
}
